<script setup lang="ts">
/**
 * GInputAutoSuggest - A search-style autosuggest input
 *
 * Uses Headless UI Combobox with floating dropdown on all screen sizes.
 *
 * Unlike GSheetSelectInput, this component does NOT hold selection state.
 * It emits `select` when an item is picked, then clears the input.
 * Use this for "search and add to external list" patterns.
 */
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/vue'
import { Float } from '@headlessui-float/vue'
import { buildRegExp, sortedTokensFor } from '@consumer/services/filter'
import { typeaheadDebounce } from '@shared/debounce'
import type { PathHelper } from '@shared/services/api'
import { faSearch } from '@fortawesome/pro-light-svg-icons'
import { faXmark } from '@fortawesome/pro-regular-svg-icons'

// =============================================================================
// Props & Emits
// =============================================================================

const {
  placeholder = 'Search...',
  fetchOptions,
  labelProp = 'label',
  valueProp = 'value',
  name,
  pill = true,
  allowCreate = false,
} = defineProps<{
  placeholder?: string
  fetchOptions: PathHelper
  labelProp?: string
  valueProp?: string
  name?: string
  pill?: boolean
  allowCreate?: boolean
}>()

const emit = defineEmits<{
  select: [item: any]
}>()

// =============================================================================
// State
// =============================================================================

let filteringBy = $ref('')
let remoteItems = $ref<any[]>()
let fetchingRemoteItems = $ref(false)

// =============================================================================
// Refs
// =============================================================================

const comboboxInputRef = ref<any>()

// =============================================================================
// Computed
// =============================================================================

const hasText = $computed(() => filteringBy.length > 0)

const hasResults = $computed(() => !fetchingRemoteItems && !!remoteItems && remoteItems.length > 0)

const CREATE_SENTINEL = '__create__'

const showEmptyState = $computed(() =>
  !fetchingRemoteItems
  && (!remoteItems || remoteItems.length === 0)
  && filteringBy.length > 0,
)

const hasDropdownContent = $computed(() =>
  fetchingRemoteItems || hasResults || showEmptyState,
)

// =============================================================================
// Remote Fetch
// =============================================================================

const debouncedFetch = typeaheadDebounce(async (term: string) => {
  if (!term.length) {
    remoteItems = undefined
    fetchingRemoteItems = false
    return
  }

  fetchingRemoteItems = true
  try {
    remoteItems = await fetchOptions?.({ query: { term }, visit: false })
  }
  catch (error) {
    console.error('Error fetching remote items:', error)
  }
  fetchingRemoteItems = false
})

watch($$(filteringBy), term => debouncedFetch(term))

// =============================================================================
// Handlers
// =============================================================================

function onComboboxChange (item: any) {
  if (!item) return
  if (item === CREATE_SENTINEL) {
    createFromInput()
    return
  }
  emit('select', item)
  clearInput()
}

function onInputChange (event: Event) {
  filteringBy = (event.target as HTMLInputElement).value
}

function createFromInput () {
  const term = filteringBy.trim()
  if (!term) return
  emit('select', { [labelProp]: term, [valueProp]: term })
  clearInput()
}

function clearInput () {
  filteringBy = ''
  remoteItems = undefined
  if (comboboxInputRef.value) {
    const input = comboboxInputRef.value.$el ?? comboboxInputRef.value
    if (input) input.value = ''
  }
}

// =============================================================================
// Utilities
// =============================================================================

function escapeHtml (str: string) {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

function highlightLabel (label: string) {
  const escaped = escapeHtml(label)
  if (!filteringBy) return escaped
  const tokens = sortedTokensFor(filteringBy)
  let result = escaped
  for (const token of tokens) {
    result = result.replace(buildRegExp(token), '<mark class="bg-yellow-100">$1</mark>')
  }
  return result
}
</script>

<template>
  <Combobox
    :modelValue="null"
    as="div"
    class="g-auto-suggest-desktop"
    @update:modelValue="onComboboxChange"
  >
    <Float
      placement="bottom-start"
      :offset="8"
      flip
      portal
      adaptiveWidth
      enter="transition duration-200 ease-out"
      enterFrom="opacity-0 scale-95 origin-top"
      enterTo="opacity-100 scale-100 origin-top"
      leave="transition duration-150 ease-in"
      leaveFrom="opacity-100 scale-100 origin-top"
      leaveTo="opacity-0 scale-95 origin-top"
    >
      <div class="g-auto-suggest-input-wrapper" :class="{ pill }" :data-name="name">
        <GIcon :icon="faSearch" class="text-v2-tertiary text-sm shrink-0"/>

        <ComboboxInput
          ref="comboboxInputRef"
          class="g-auto-suggest-input"
          :placeholder="placeholder"
          :displayValue="() => filteringBy"
          @change="onInputChange"
        />

        <button
          v-if="hasText"
          type="button"
          class="g-auto-suggest-clear"
          @mousedown.prevent="clearInput"
        >
          <GIcon :icon="faXmark" class="text-v2-tertiary text-sm"/>
        </button>
      </div>

      <ComboboxOptions
        v-show="hasDropdownContent"
        class="g-auto-suggest-options"
        :class="{ pill }"
        :data-name="name"
      >
        <div v-if="fetchingRemoteItems" class="g-auto-suggest-option loading">
          <LoadingIndicator/>
        </div>

        <template v-else>
          <ComboboxOption
            v-for="item in (remoteItems || [])"
            v-slot="{ active }"
            :key="item[valueProp]"
            :value="item"
            as="template"
          >
            <li class="g-auto-suggest-option" :class="{ active }">
              <slot name="itemPre" :item="item" :filteringBy="filteringBy"/>
              <div class="flex-1">
                <span v-html="highlightLabel(item[labelProp])"/>
              </div>
            </li>
          </ComboboxOption>
        </template>

        <div v-if="showEmptyState && !allowCreate" class="g-auto-suggest-option empty">
          <slot name="empty" :term="filteringBy.trim()">
            No results found
          </slot>
        </div>

        <ComboboxOption
          v-if="allowCreate && (showEmptyState || hasResults)"
          v-slot="{ active }"
          :value="CREATE_SENTINEL"
          as="template"
        >
          <li class="g-auto-suggest-option empty" :class="{ active }">
            <slot
              :name="showEmptyState ? 'empty' : 'afterItems'"
              :term="filteringBy.trim()"
            >
              Add "{{ filteringBy.trim() }}"
            </slot>
          </li>
        </ComboboxOption>
      </ComboboxOptions>
    </Float>
  </Combobox>
</template>

<style lang="scss">
.g-auto-suggest-desktop {
  @apply relative w-full;
}

.g-auto-suggest-input-wrapper {
  @apply relative flex items-center gap-2;
  @apply bg-white font-medium text-navy;

  border: 1px solid var(--grey-primary);
  border-radius: theme("borderRadius.v2md");
  padding: 0.875rem 1.25rem;
  transition: border-color 0.15s;

  &:hover {
    border-color: var(--theme-color-primary-base);
  }

  &:focus-within {
    border-color: var(--theme-color-primary-base);
  }

  &.pill {
    border-radius: 9999px;
  }
}

.g-auto-suggest-input {
  @apply flex-1 min-w-0 bg-transparent outline-none;
  @apply placeholder:text-v2-tertiary placeholder:opacity-100;
}

.g-auto-suggest-clear {
  @apply flex items-center justify-center size-5 shrink-0;
}

.g-auto-suggest-options {
  @apply py-1 bg-white drop-shadow-dropdown overflow-auto;

  border: 1px solid var(--grey-primary);
  border-radius: theme("borderRadius.md");
  max-height: 200px;
  min-width: 200px;

  &.pill {
    @apply py-3;

    border-radius: theme("borderRadius.v2lg");
  }
}

.g-auto-suggest-option {
  @apply flex items-center gap-2 px-4 py-3;
  @apply cursor-pointer text-navy font-medium select-none;

  &.active {
    @apply bg-surface-grey;
  }

  &.loading,
  &.empty {
    @apply text-v2-tertiary cursor-default;
  }
}
</style>
